import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { EndPointService } from 'src/app/core/services/end-point.service';
import { PagingState } from 'src/app/shared/framework/paging/PagingState';
import { ClusterStatus } from '../models/ClusterStatus';
import { Competence } from '../models/Competence';
import { CompetenceCluster } from '../models/CompetenceCluster';
import { CompetenceStatus } from '../models/CompetenceStatus';

@Injectable({
  providedIn: 'root',
})
export class CompetenceService {
  private _apiBase = '/api/competences/';

  constructor(private _endPointService: EndPointService, private http: HttpClient) {
    this._endPointService.registerEndPoint<Competence[]>('competences', `${this._apiBase}`);
    this._endPointService.registerEndPoint<number>('countcompetences', `${this._apiBase}count`);
    this._endPointService.registerEndPoint<Competence>('competence', `${this._apiBase}getbyid`);

    this._endPointService.registerEndPoint<CompetenceCluster[]>('clusters', `${this._apiBase}clusters`);
    this._endPointService.registerEndPoint<number>('countclusters', `${this._apiBase}clusters/count`);

    this._endPointService.registerEndPoint<Competence[]>('competences_fordevgoals', `${this._apiBase}fordevelopmentobjective`);
    this._endPointService.registerEndPoint<number>('countcompetences_fordevgoals', `${this._apiBase}countfordevelopmentobjective`);
  }

  // #region general
  public getStatusClass(status: ClusterStatus) {
    switch (status) {
      case ClusterStatus.InSetup: return '#EE7622';
      case ClusterStatus.Active: return '#28A745';
      case ClusterStatus.Inactive: return '#bfbfbf';
      case ClusterStatus.Deleted: return '#ff0000';
      default: return '#EE7622'
    }
  }

  public getStatusName(status: ClusterStatus) {
    switch (status) {
      case ClusterStatus.InSetup: return 'cluster_setup_in_progress';
      case ClusterStatus.Active: return 'active';
      case ClusterStatus.Inactive: return 'inactive';
      case ClusterStatus.Deleted: return 'deleted';
      case 3: return 'all';
      default: return 'cluster_setup_in_progress'
    }
  }

  public getCompetenceStatusClass(status: CompetenceStatus) {
    switch (status) {
      case CompetenceStatus.InSetup: return '#EE7622';
      case CompetenceStatus.Active: return '#28A745';
      case CompetenceStatus.Inactive: return '#bfbfbf';
      case CompetenceStatus.Deleted: return '#ff0000';
      default: return '#EE7622'
    }
  }

  public getCompetenceStatusName(status: CompetenceStatus) {
    switch (status) {
      case CompetenceStatus.InSetup: return 'competence_setup_in_progress';
      case CompetenceStatus.Active: return 'active';
      case CompetenceStatus.Inactive: return 'inactive';
      case CompetenceStatus.Deleted: return 'deleted';
      case 3: return 'all';
      default: return 'competence_setup_in_progress'
    }
  }
  // #endregion general

  public getCompetences(paging?: PagingState, fetch?: boolean): Observable<Competence[]> {
    return this._endPointService.call<Competence[]>('competences', paging, fetch);
  }

  public countCompetences(searchString: string, fetch?: boolean): Observable<Competence[]> {
    return this._endPointService.call<Competence[]>('countcompetences', { searchString }, fetch);
  }

  public getCompetence(id: number, fetch?: boolean): Observable<Competence> {
    return this._endPointService.call<Competence>('competence', id, fetch);
  }


  public getClusters(paging?: PagingState, fetch?: boolean): Observable<CompetenceCluster[]> {
    return this._endPointService.call<CompetenceCluster[]>('clusters', paging, fetch);
  }

  public countClusters(searchString: string, fetch?: boolean): Observable<number> {
    return this._endPointService.call<number>('countclusters', { searchString }, fetch);
  }

  public getCompetencesForDevelopmentGoals(paging?: PagingState, functionId?: number, basedOnFunction?: boolean, fetch?: boolean): Observable<Competence[]> {
    return this._endPointService.call<Competence[]>('competences_fordevgoals', { pagingState: paging, functionId, basedOnFunction }, fetch);
  }

  public countCompetencesForDevelopmentGoals(searchString: string, functionId?: number, basedOnFunction?: boolean, fetch?: boolean): Observable<Competence[]> {
    return this._endPointService.call<Competence[]>('countcompetences_fordevgoals', { search: { searchString }, functionId, basedOnFunction }, fetch);
  }


  public getClustersAndCompetences(): Observable<CompetenceCluster[]> {
    return this.http.post<CompetenceCluster[]>('/api/competences/clusters-list', null);
  }

  public changeClusterStatus(id: number, status: ClusterStatus): Observable<void> {
    return this.http.post<void>('/api/competences/changeclusterstatus/' + id, status);
  }

  public updateCluster(cluster: CompetenceCluster): Observable<void> {
    return this.http.post<void>('/api/competences/updatecluster', cluster);
  }

  public createCluster(cluster: CompetenceCluster): Observable<number> {
    return this.http.post<number>('/api/competences/createcluster', cluster);
  }

  public changeCompetenceStatus(id: number, status: CompetenceStatus): Observable<void> {
    return this.http.post<void>('/api/competences/changecompetencestatus/' + id, status);
  }

  public updateCompetence(competence: Competence): Observable<void> {
    return this.http.post<void>('/api/competences/update', competence);
  }

  public createCompetence(competence: Competence): Observable<number> {
    return this.http.post<number>('/api/competences/createcompetence', competence);
  }

  public getCompetenceLinks(competenceId: number): Observable<Competence> {
    return this.http.post<Competence>('/api/competences/competencelevellinks/' + competenceId, null);
  }
}
